<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { SvelteSet } from 'svelte/reactivity';
  import { Check, X } from 'lucide-svelte';
  import { hrInboxApi, hrLeaveApi, hrUsersApi } from '$lib/api/hr';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import SelectionBar from '$lib/components/ui/SelectionBar.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import ApprovalCard, { type ApprovalMeta } from '$lib/components/ui/ApprovalCard.svelte';
  import StatusBadge, { type BadgeKind } from '$lib/components/ui/StatusBadge.svelte';
  import type { HRRequest } from '$lib/types';

  const queryClient = useQueryClient();
  let typeFilter = $state('');
  let statusFilter = $state('pending');
  let userFilter = $state('');
  let page = $state(1);
  const perPage = 20;

  const query = createQuery(() => ({
    queryKey: ['hr-inbox', typeFilter, statusFilter, userFilter, page],
    queryFn: () =>
      hrInboxApi.list({ type: typeFilter, status: statusFilter, user_id: userFilter, page, per_page: perPage })
  }));
  const usersQuery = createQuery(() => ({
    queryKey: ['hr-users'],
    queryFn: () => hrUsersApi.list()
  }));
  // Backs the typeless-pending-leave row's inline "set type" selector — see
  // setLeaveType below.
  const leaveTypesQuery = createQuery(() => ({
    queryKey: ['leave-types'],
    queryFn: () => hrLeaveApi.listTypes()
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['hr-inbox'] });
  }

  const userName = $derived((id: number) => {
    const u = (usersQuery.data ?? []).find((u) => u.id === id);
    return u ? u.name || u.email : `User #${id}`;
  });

  function applyFilters() {
    page = 1;
    selected.clear();
  }

  const TYPE_LABEL: Record<string, string> = {
    leave: 'Leave',
    late: 'Late',
    early_leaving: 'Early leaving',
    other: 'Other'
  };

  const STATUS_KIND: Record<string, BadgeKind> = {
    pending: 'pending',
    approved: 'approved',
    rejected: 'rejected',
    logged: 'logged'
  };

  // Only pending requests need review — logged (late only) never needs
  // approval, and approved/rejected are already final.
  function reviewable(r: { status: string }): boolean {
    return r.status === 'pending';
  }

  // Legacy/typeless pending leave requests (filed before a leave type was
  // required, see buildLeaveRequest in hr_requests.go) need HR to backfill a
  // type before approving — otherwise approveHR's ledger write never fires.
  function needsLeaveType(r: HRRequest): boolean {
    return r.type === 'leave' && r.status === 'pending' && r.leave_type_id == null;
  }

  async function setLeaveType(id: number, leaveTypeId: number) {
    try {
      await hrInboxApi.setLeaveType(id, leaveTypeId);
      refresh();
      toast.success('Leave type set.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  // No filter beyond the default "Pending" view — the everyday inbox state.
  const isDefaultView = $derived(statusFilter === 'pending' && !typeFilter && !userFilter);

  const selected = new SvelteSet<number>();
  function toggle(id: number) {
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
  }
  const selectableIds = $derived((query.data?.hr_requests ?? []).filter(reviewable).map((r) => r.id));
  function selectAll() {
    selectableIds.forEach((id) => selected.add(id));
  }

  // ==========================================================
  // Decisions go through a confirm step rendered as an ApprovalCard rather
  // than firing straight off the row button. Approving leave writes to
  // someone's balance ledger and there is no undo in the API, so the
  // reviewer gets the subject of the decision — who, what, which dates —
  // in front of them at the moment they commit, not only afterwards in a
  // toast. Same card serves the single-row and bulk paths.
  // ==========================================================
  type Decision = { kind: 'approve' | 'reject'; requests: HRRequest[] };
  let decision = $state<Decision | null>(null);
  let deciding = $state(false);
  let decisionError = $state('');

  function ask(kind: 'approve' | 'reject', requests: HRRequest[]) {
    if (requests.length === 0) return;
    decisionError = '';
    decision = { kind, requests };
  }
  function askBulk(kind: 'approve' | 'reject') {
    ask(kind, (query.data?.hr_requests ?? []).filter((r) => selected.has(r.id)));
  }

  function dateRange(r: HRRequest): string {
    const start = r.start_date.slice(0, 10);
    const end = r.end_date?.slice(0, 10);
    return end && end !== start ? `${start} – ${end}` : start;
  }
  function typeLabel(r: HRRequest): string {
    return `${TYPE_LABEL[r.type]}${r.leave_type_name ? ` — ${r.leave_type_name}` : ''}`;
  }

  const isBulk = $derived((decision?.requests.length ?? 0) > 1);

  // Approving a leave request that never got a type back-filled silently
  // no-ops the ledger write (see needsLeaveType) — worth saying before the
  // decision, not after it fails.
  const untypedCount = $derived(
    decision?.kind === 'approve' ? decision.requests.filter(needsLeaveType).length : 0
  );

  const decisionMeta = $derived.by<ApprovalMeta[]>(() => {
    const rows = decision?.requests ?? [];
    if (rows.length === 0) return [];
    if (rows.length === 1) {
      const r = rows[0];
      const meta: ApprovalMeta[] = [
        { label: 'Employee', value: userName(r.user_id) },
        { label: 'Type', value: typeLabel(r) },
        { label: 'Date(s)', value: dateRange(r) }
      ];
      if (r.business_days != null) meta.push({ label: 'Business days', value: String(r.business_days) });
      meta.push({ label: 'Reason', value: r.reason || '—' });
      return meta;
    }
    const people = new Set(rows.map((r) => r.user_id));
    const types = [...new Set(rows.map((r) => TYPE_LABEL[r.type]))];
    return [
      { label: 'Requests', value: String(rows.length) },
      { label: 'People', value: String(people.size) },
      { label: 'Types', value: types.join(', ') }
    ];
  });

  async function commit() {
    if (!decision) return;
    const { kind, requests } = decision;
    deciding = true;
    decisionError = '';
    try {
      if (requests.length === 1) {
        const id = requests[0].id;
        if (kind === 'approve') await hrInboxApi.approve(id);
        else await hrInboxApi.reject(id);
        selected.delete(id);
        toast.success(kind === 'approve' ? 'Request approved.' : 'Request rejected.');
      } else {
        const ids = requests.map((r) => r.id);
        const res = kind === 'approve' ? await hrInboxApi.bulkApprove(ids) : await hrInboxApi.bulkReject(ids);
        selected.clear();
        toast.success(`${res.count} request(s) ${kind === 'approve' ? 'approved' : 'rejected'}.`);
      }
      refresh();
      decision = null;
    } catch (err) {
      decisionError = getErrorMessage(err);
    } finally {
      deciding = false;
    }
  }
</script>

<div class="tym-page {selected.size > 0 ? 'tym-dock-gap' : ''}">
  <Toolbar>
    {#snippet filters()}
      <!-- Three fixed-width selects overflowed a phone row; let them share
           the width two-up and wrap, and keep the desktop row as it was. -->
      <div class="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        <select
          class="input w-auto min-w-[8rem] flex-1 sm:min-w-[9rem] sm:flex-none"
          aria-label="Type"
          bind:value={typeFilter}
          onchange={applyFilters}
        >
          <option value="">All types</option>
          <option value="leave">Leave</option>
          <option value="late">Late</option>
          <option value="early_leaving">Early leaving</option>
          <option value="other">Other</option>
        </select>
        <select
          class="input w-auto min-w-[8rem] flex-1 sm:min-w-[9rem] sm:flex-none"
          aria-label="Status"
          bind:value={statusFilter}
          onchange={applyFilters}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="logged">Logged</option>
        </select>
        <select
          class="input w-auto min-w-[8rem] flex-1 sm:min-w-[9rem] sm:flex-none"
          aria-label="User"
          bind:value={userFilter}
          onchange={applyFilters}
        >
          <option value="">All users</option>
          {#each usersQuery.data ?? [] as u (u.id)}
            <option value={String(u.id)}>{u.name || u.email}</option>
          {/each}
        </select>
      </div>
    {/snippet}
    {#snippet actions()}
      {#if selected.size > 0}
        <!-- Desktop keeps the cluster in the toolbar, where it has always
             been and where nothing is out of reach; phones get the docked
             SelectionBar below instead of a second copy of this. -->
        <div class="hidden items-center gap-2.5 rounded-full border border-toyama-teal bg-toyama-teal/10 py-1 pl-3 pr-1.5 sm:flex">
          <span class="text-[12px] font-bold text-toyama-teal">{selected.size} selected</span>
          <button type="button" class="btn-primary !min-h-[28px] !rounded-full !px-3 !py-0 !text-[12px]" onclick={() => askBulk('approve')}>
            <Check class="h-3.5 w-3.5" /> Approve
          </button>
          <button type="button" class="btn-danger !min-h-[28px] !rounded-full !border-0 !px-3 !py-0 !text-[12px]" onclick={() => askBulk('reject')}>
            <X class="h-3.5 w-3.5" /> Reject
          </button>
        </div>
      {:else if selectableIds.length > 0}
        <button type="button" class="btn-secondary" onclick={selectAll}>Select all pending</button>
      {/if}
    {/snippet}
  </Toolbar>

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load requests.</div>
  {:else if query.data}
    <DataTable
      mobileCards
      columns={[
        { key: 'select', label: '', width: '32px', cell: selectCell },
        { key: 'user', label: 'User', cell: userCell },
        { key: 'type', label: 'Type', cell: typeCell },
        { key: 'dates', label: 'Date(s)', cell: datesCell },
        { key: 'status', label: 'Status', cell: statusCell },
        { key: 'reason', label: 'Reason', cell: reasonCell },
        { key: 'actions', label: '', align: 'right' as const, cell: actionsCell }
      ]}
      rows={query.data.hr_requests}
      rowKey={(r) => r.id}
      emptyTitle={isDefaultView ? 'No requests to review — all caught up' : 'Nothing matches these filters'}
      emptyBody={isDefaultView
        ? 'New leave, late and early-leaving requests will land here.'
        : 'Try a different type, status or user.'}
      footer={query.data.total > 0 ? footer : undefined}
    />
  {/if}

  <SelectionBar count={selected.size} onClear={() => selected.clear()}>
    {#snippet actions()}
      <button type="button" class="btn-primary tym-touch !px-4" onclick={() => askBulk('approve')}>
        <Check class="h-4 w-4" /> Approve
      </button>
      <button type="button" class="btn-danger tym-touch !px-4" onclick={() => askBulk('reject')}>
        <X class="h-4 w-4" /> Reject
      </button>
    {/snippet}
  </SelectionBar>

  {#if decision}
    <Modal
      title={decision.kind === 'approve' ? 'Approve request' : 'Reject request'}
      onClose={() => {
        if (!deciding) decision = null;
      }}
    >
      <ApprovalCard
        tone={decision.kind === 'approve' ? 'neutral' : 'danger'}
        title={isBulk
          ? `${decision.requests.length} requests will be ${decision.kind === 'approve' ? 'approved' : 'rejected'}`
          : `${typeLabel(decision.requests[0])} — ${userName(decision.requests[0].user_id)}`}
        body={decision.kind === 'approve'
          ? 'Approving posts the leave to the employee’s balance ledger. There is no undo.'
          : 'The employee keeps their balance, and sees the request as rejected.'}
        meta={decisionMeta}
        confirmLabel={decision.kind === 'approve' ? 'Approve' : 'Reject'}
        busy={deciding}
        error={decisionError}
        onCancel={() => (decision = null)}
        onConfirm={commit}
      >
        {#if untypedCount > 0}
          <p class="rounded-lg bg-warning/10 px-3 py-2 text-[12.5px] text-warning">
            {untypedCount === 1
              ? 'This request has no leave type set, so approving it will not move any balance. Set a type first.'
              : `${untypedCount} of these have no leave type set, so approving them will not move any balance.`}
          </p>
        {/if}
      </ApprovalCard>
    </Modal>
  {/if}
</div>

{#snippet selectCell(r: HRRequest)}
  {#if reviewable(r)}
    <input
      type="checkbox"
      checked={selected.has(r.id)}
      onchange={() => toggle(r.id)}
      onclick={(e) => e.stopPropagation()}
      class="h-5 w-5 accent-toyama-teal sm:h-4 sm:w-4"
      aria-label={`Select request from ${userName(r.user_id)}`}
    />
  {/if}
{/snippet}
{#snippet userCell(r: HRRequest)}
  <span class="muted">{userName(r.user_id)}</span>
{/snippet}
{#snippet typeCell(r: HRRequest)}
  <span class="font-medium text-navy">{TYPE_LABEL[r.type]}{r.leave_type_name ? ` — ${r.leave_type_name}` : ''}</span>
  {#if needsLeaveType(r)}
    <select
      class="input mt-1 !py-1 !text-[16px] sm:!text-[12px]"
      aria-label={`Set leave type for request from ${userName(r.user_id)}`}
      onchange={(e) => setLeaveType(r.id, Number(e.currentTarget.value))}
      onclick={(e) => e.stopPropagation()}
    >
      <option value="">Set leave type...</option>
      {#each (leaveTypesQuery.data ?? []).filter((t) => t.active) as t (t.id)}
        <option value={t.id}>{t.name}</option>
      {/each}
    </select>
  {/if}
{/snippet}
{#snippet datesCell(r: HRRequest)}
  <span class="muted"
    >{r.start_date.slice(0, 10)}{r.end_date && r.end_date.slice(0, 10) !== r.start_date.slice(0, 10)
      ? ` – ${r.end_date.slice(0, 10)}`
      : ''}</span
  >
{/snippet}
{#snippet statusCell(r: HRRequest)}
  <StatusBadge kind={STATUS_KIND[r.status]} label={r.status} />
{/snippet}
{#snippet reasonCell(r: HRRequest)}
  <span class="muted">{r.reason || '—'}</span>
{/snippet}
{#snippet actionsCell(r: HRRequest)}
  {#if reviewable(r)}
    <div class="flex justify-end gap-1.5" onclick={(e) => e.stopPropagation()} role="presentation">
      <button type="button" class="btn-secondary tym-touch !px-2.5 !py-1.5 !text-[12.5px]" onclick={() => ask('approve', [r])}>
        Approve
      </button>
      <button type="button" class="btn-danger tym-touch !px-2.5 !py-1.5 !text-[12.5px]" onclick={() => ask('reject', [r])}>
        Reject
      </button>
    </div>
  {/if}
{/snippet}
{#snippet footer()}
  <Pagination {page} {perPage} count={query.data?.total ?? 0} onChange={(p) => (page = p)} />
{/snippet}
